import type { AxiosResponse } from 'axios'

import apiClient from './client'
import API from '@/api/API'
import DASHBOARD_API from '@/api/DASHBOARD_API'
import { getAuthApiUrl, getConfig } from '@/lib/config'
import { DEFAULT_LOGIN_ROUTE } from '@/lib/constants'
import { redirectToAppRoute } from '@/lib/navigation'
import type {
  DesktopMaterialItem,
  MaterialDirNode,
  MaterialFilterType,
  MaterialItemResponse,
  MaterialListResponse,
  MaterialRecordEnvelope,
  MaterialRecordResult,
  MaterialRecordResultItem,
  MaterialStatsResponse,
  MaterialType,
} from '@/lib/types/api'

export type DashboardMaterialType = 'img' | 'video' | 'audio' | 'file'
type DashboardIdentifier = string | number

interface DashboardEnvelope<T> {
  code?: string | number
  data?: T
  msg?: string
  message?: string
}

interface DashboardDirNode {
  idDir?: DashboardIdentifier
  idParentDir?: DashboardIdentifier | null
  name?: string
  materialType?: DashboardMaterialType
  /** dashboard-main 后端目录类型：ROOT=全部素材 / SYSTEM=系统目录 / USER=用户分组 */
  dirType?: 'ROOT' | 'SYSTEM' | 'USER' | null
  dirLevel?: number
  count?: number
  children?: DashboardDirNode[]
}

interface DashboardMaterialInfo {
  usedSize?: number
  fileNum?: number
}

interface DashboardMaterialRecord {
  idDir?: DashboardIdentifier
  idParentDir?: DashboardIdentifier | null
  name?: string
  fileUrl?: string
  fileUrlShow?: string
  frontUrl?: string
  frontUrlShow?: string
  fileUploadFlag?: string
  fileUpdateParams?: string
  materialType?: DashboardMaterialType
  materialTypeDesc?: string
  storageType?: string
  ext?: string
  /** 尺寸，KB */
  size?: number
  /** 时长 */
  timeLength?: number
  extractedText?: string
}

interface DashboardUploadPrepareResult {
  provider?: string
  uploadMode?: string
  region?: string
  accessKeyId?: string
  accessKeySecret?: string
  stsToken?: string
  expiration?: string
  bucket?: string
  dir?: string
  filename?: string
  domain?: string
  cdnDomain?: string
  uploadUrl?: string
  uploadMethod?: string
  // VOD-specific fields (returned by /fs/materialUpload/vodPrepare for Aliyun VOD)
  uploadAuth?: string
  uploadAddress?: string
  bucketFileName?: string
  vodPrefix?: string
  vodType?: string
  procedure?: string
  videoId?: string
  requestId?: string
  userId?: string
}

export interface UploadedDashboardFile {
  fileUrl: string
  url: string
  name: string
  size: number
  materialType: DashboardMaterialType
  storageType: string
  key: string
}

const MATERIAL_TYPE_TO_DASHBOARD: Record<MaterialType, DashboardMaterialType> = {
  image: 'img',
  video: 'video',
  audio: 'audio',
  file: 'file',
}

const DASHBOARD_TYPE_TO_MATERIAL: Record<DashboardMaterialType, MaterialType> = {
  img: 'image',
  video: 'video',
  audio: 'audio',
  file: 'file',
}

export const MATERIAL_EXTENSIONS: Record<DashboardMaterialType, string[]> = {
  img: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff', 'tif', 'avif', 'heif', 'heic'],
  video: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg', '3gp'],
  audio: ['mp3', 'wav', 'aac', 'ogg', 'flac', 'wma', 'm4a', 'ape', 'aiff'],
  file: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'zip', 'rar', '7z'],
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function unwrapDashboardResponse<T>(body: DashboardEnvelope<T> | T): T {
  if (isRecord(body) && 'code' in body) {
    const envelope = body as DashboardEnvelope<T>
    const code = Number(envelope.code)
    if (Number.isFinite(code) && code >= 200 && code < 300) {
      return envelope.data as T
    }
    throw new Error(envelope.msg || envelope.message || '请求失败')
  }

  if (isRecord(body) && 'data' in body) {
    return (body as DashboardEnvelope<T>).data as T
  }

  return body as T
}

const DASHBOARD_NO_LOGIN_MSG = '未登录'

async function dashboardPost<T>(url: string, data?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.post<DashboardEnvelope<T> | T>(url, data, {
    baseURL: await getAuthApiUrl(),
    withCredentials: true,
    headers: {
      Source: DASHBOARD_API.SOURCE,
      'Source-App': DASHBOARD_API.SOURCE_APP,
    },
  })

  try {
    return unwrapDashboardResponse<T>(response.data)
  } catch (error) {
    if (error instanceof Error && error.message === DASHBOARD_NO_LOGIN_MSG) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth-storage')
        redirectToAppRoute(DEFAULT_LOGIN_ROUTE)
      }
    }
    throw error
  }
}

function toStringId(value: DashboardIdentifier | null | undefined): string {
  return value === undefined || value === null ? '' : String(value)
}

function toDashboardId(value?: string | null): DashboardIdentifier | undefined {
  if (!value) return undefined
  const numeric = Number(value)
  return Number.isFinite(numeric) && String(numeric) === value ? numeric : value
}

function toDashboardMaterialType(type: MaterialType): DashboardMaterialType {
  return MATERIAL_TYPE_TO_DASHBOARD[type] ?? 'file'
}

function toDashboardMaterialFilter(type: MaterialFilterType): DashboardMaterialType | undefined {
  return type === 'all' ? undefined : toDashboardMaterialType(type)
}

function toMaterialType(type?: DashboardMaterialType): MaterialType {
  return type ? DASHBOARD_TYPE_TO_MATERIAL[type] ?? 'file' : 'file'
}

function normalizeDir(node: DashboardDirNode, fallbackType: MaterialType): MaterialDirNode {
  const materialType = toMaterialType(node.materialType) || fallbackType
  const ownCount = Number(node.count ?? 0)
  const normalizedChildren = (node.children ?? []).map((child) => normalizeDir(child, materialType))
  const totalCount = ownCount + normalizedChildren.reduce((sum, child) => sum + (child.count || 0), 0)
  return {
    id: toStringId(node.idDir),
    name: node.name ?? '未命名分组',
    parent_id: node.idParentDir === undefined || node.idParentDir === null ? null : toStringId(node.idParentDir),
    material_type: materialType,
    dir_type: node.dirType === 'ROOT' || node.dirType === 'SYSTEM' || node.dirType === 'USER' ? node.dirType : null,
    dir_level: node.dirLevel ?? 0,
    count: totalCount,
    children: normalizedChildren,
  }
}

function normalizeItem(record: DashboardMaterialRecord, fallbackType: MaterialType): MaterialItemResponse {
  const materialType = toMaterialType(record.materialType) || fallbackType
  // 严格对齐 MaterialListVo.java 真实字段，不发明 cover_url / preview_url / download_url：
  //  - 视频/音频封面 = frontUrl（腾讯 VOD 动图 GIF）；无则显示默认占位图
  //  - 图片缩略图 = fileUrlShow（CDN 可访问图）；无则显示默认占位图
  return {
    id: toStringId(record.idDir),
    name: record.name ?? '未知文件',
    material_type: materialType,
    dir_id: record.idParentDir === undefined || record.idParentDir === null ? null : toStringId(record.idParentDir),
    file_size: Number(record.size ?? 0),
    file_type: record.ext ?? null,
    ext: record.ext ?? null,
    file_url: record.fileUrl || null,
    file_url_show: record.fileUrlShow || null,
    front_url: record.frontUrl || null,
    front_url_show: record.frontUrlShow || null,
    file_upload_flag: record.fileUploadFlag || null,
    file_update_params: record.fileUpdateParams || null,
    material_type_desc: record.materialTypeDesc || null,
    storage_type: record.storageType || null,
    time_length: record.timeLength ?? null,
    extracted_text: record.extractedText || null,
  }
}

function getFileExtension(file: File): string {
  return file.name.split('.').pop()?.toLowerCase() || ''
}

function detectDashboardMaterialType(file: File, fallback: DashboardMaterialType): DashboardMaterialType {
  const ext = getFileExtension(file)
  const entry = (Object.entries(MATERIAL_EXTENSIONS) as Array<[DashboardMaterialType, string[]]>)
    .find(([, extensions]) => extensions.includes(ext))
  return entry?.[0] ?? fallback
}

function trimSlash(value = '') {
  return value.replace(/\/+$/, '')
}

/** Java material_record.name 上限（与 /record 契约一致）。 */
const RECORD_NAME_MAX_LEN = 64

/**
 * 清洗 /record 入库的素材名：移除电商 CDN 特殊字段（Taobao/Tmall 裁剪参数、
 * 卖家信息后缀）+ 截断到 64 字符，避免后端 422 string_too_long。
 * 例："O1CN01ff1cYX1YwqRLDm5v4~crop,38,0,675,900~_!!2217433253124.png_.webp"
 *   → "O1CN01ff1cYX1YwqRLDm5v4.png_.webp"
 */
export function sanitizeMaterialName(name: string): string {
  let cleaned = (name ?? '').trim()
  if (!cleaned) return cleaned
  // 去掉 Taobao/Tmall 卖家信息后缀 `~_!!数字`
  cleaned = cleaned.replace(/~_!!\d+/g, '')
  // 去掉 CDN 裁剪参数段 `~crop,38,0,675,900`
  cleaned = cleaned.replace(/~crop,[\d,]+/g, '')
  // 剩余孤立 `~` 清理
  cleaned = cleaned.replace(/~/g, '')
  // 去除控制字符与非法文件名字符
  cleaned = cleaned
    .split('')
    .filter((ch) => ch.charCodeAt(0) >= 32)
    .filter((ch) => !'<>:"/\\|?*'.includes(ch))
    .join('')
    .replace(/[. ]+$/, '')
  if (!cleaned) return cleaned

  if (cleaned.length <= RECORD_NAME_MAX_LEN) return cleaned

  // 超长截断：优先保留扩展名
  const dot = cleaned.lastIndexOf('.')
  if (dot > 0 && cleaned.length - dot < RECORD_NAME_MAX_LEN) {
    const suffix = cleaned.slice(dot)
    cleaned = `${cleaned.slice(0, RECORD_NAME_MAX_LEN - suffix.length)}${suffix}`.replace(/[. ]+$/, '')
  } else {
    cleaned = cleaned.slice(0, RECORD_NAME_MAX_LEN).replace(/[. ]+$/, '')
  }
  return cleaned
}

function buildObjectKey(prepare: DashboardUploadPrepareResult = {}) {
  const dir = trimSlash(prepare.dir || '')
  const filename = prepare.filename || ''
  return `${dir}/${filename}`.replace(/^\/+/, '')
}

function buildDirectFileUrl(prepare: DashboardUploadPrepareResult = {}, objectKey = buildObjectKey(prepare)) {
  let baseUrl = trimSlash(prepare.cdnDomain || prepare.domain || '')
  if (baseUrl && !/^https?:\/\//i.test(baseUrl) && !baseUrl.startsWith('//')) {
    baseUrl = `https://${baseUrl}`
  }
  return baseUrl ? `${baseUrl}/${objectKey}` : objectKey
}

function parseExpiration(value?: string) {
  const seconds = value ? Math.floor(new Date(value).getTime() / 1000) : 0
  if (Number.isFinite(seconds) && seconds > 0) {
    return seconds
  }
  return Math.floor(Date.now() / 1000) + 900
}

async function uploadByAliyun(file: File, prepare: DashboardUploadPrepareResult) {
  const objectKey = buildObjectKey(prepare)
  const OSS = (await import('ali-oss')).default
  const client = new OSS({
    accessKeyId: prepare.accessKeyId,
    accessKeySecret: prepare.accessKeySecret,
    stsToken: prepare.stsToken,
    region: prepare.region,
    bucket: prepare.bucket,
    secure: true,
    timeout: 600000,
  })

  await client.put(objectKey, file)
  return {
    fileUrl: objectKey,
    url: buildDirectFileUrl(prepare, objectKey),
  }
}

async function uploadByCos(file: File, prepare: DashboardUploadPrepareResult) {
  const objectKey = buildObjectKey(prepare)
  const COS = (await import('cos-js-sdk-v5')).default
  const cos = new COS({
    getAuthorization: (_options, callback) => {
      callback({
        TmpSecretId: prepare.accessKeyId || '',
        TmpSecretKey: prepare.accessKeySecret || '',
        SecurityToken: prepare.stsToken || '',
        StartTime: Math.floor(Date.now() / 1000) - 5,
        ExpiredTime: parseExpiration(prepare.expiration),
      })
    },
  })

  await new Promise((resolve, reject) => {
    cos.putObject({
      Bucket: prepare.bucket || '',
      Region: prepare.region || '',
      Key: objectKey,
      Body: file,
      ContentType: file.type || 'application/octet-stream',
    }, (err, data) => {
      if (err) {
        reject(err)
        return
      }
      resolve(data)
    })
  })

  return {
    fileUrl: objectKey,
    url: buildDirectFileUrl(prepare, objectKey),
  }
}

async function uploadDirectFile(file: File, prepare: DashboardUploadPrepareResult): Promise<{ fileUrl: string; url: string }> {
  if (!prepare.filename || !prepare.dir) {
    throw new Error('上传凭证缺少文件路径')
  }

  if (prepare.uploadMode === 'presigned_url') {
    if (!prepare.uploadUrl) {
      throw new Error('上传凭证缺少直传地址')
    }
    const response = await fetch(prepare.uploadUrl, {
      method: prepare.uploadMethod || 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    })

    if (!response.ok) {
      throw new Error(`上传失败：${response.status}`)
    }

    const objectKey = buildObjectKey(prepare)
    return {
      fileUrl: objectKey,
      url: buildDirectFileUrl(prepare, objectKey),
    }
  }

  if (prepare.provider === 'cos' || prepare.provider === 'tencent') {
    return uploadByCos(file, prepare)
  }

  return uploadByAliyun(file, prepare)
}

const ALIYUN_VOD_SDK_URL = 'https://g.alicdn.com/code/lib/alibabacloud-upload-sdk/1.0.0/aliyun-upload-sdk.min.js'
const TENCENT_VOD_SDK_URL = 'https://cdn-go.cn/cdn/vod-js-sdk-v6/latest/vod-js-sdk-v6.js'

async function loadAliyunVodSdk(): Promise<void> {
  if (typeof window === 'undefined') return
  if ((window as unknown as Record<string, unknown>).AliyunUpload) return

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = ALIYUN_VOD_SDK_URL
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('无法加载阿里云视频上传SDK'))
    document.head.appendChild(script)
  })
}

async function loadTencentVodSdk(): Promise<void> {
  if (typeof window === 'undefined') return
  if ((window as unknown as Record<string, unknown>).TcVod) return

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TENCENT_VOD_SDK_URL
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('无法加载腾讯云视频上传SDK'))
    document.head.appendChild(script)
  })
}

async function uploadViaAliyunVod(
  file: File,
  prepare: DashboardUploadPrepareResult,
): Promise<{ fileUrl: string; url: string }> {
  await loadAliyunVodSdk()

  const AliyunUpload = (window as unknown as Record<string, unknown>).AliyunUpload as Record<string, unknown> | undefined
  if (!AliyunUpload?.Vod) {
    throw new Error('阿里云视频上传SDK不可用')
  }

  const { uploadAuth, uploadAddress, bucketFileName, videoId, requestId, userId } = prepare

  if (!uploadAuth || !uploadAddress || !videoId) {
    throw new Error('VOD上传凭证不完整')
  }

  const VodConstructor = AliyunUpload.Vod as new (config: Record<string, unknown>) => {
    addFile: (file: File) => void
    startUpload: () => void
    setUploadAuthAndAddress: (uploadInfo: unknown, uploadAuth: string, uploadAddress: string, videoId: string) => void
  }

  return new Promise((resolve, reject) => {
    const uploader = new VodConstructor({
      partSize: 1048576,
      parallel: 5,
      retryCount: 3,
      retryDuration: 2,
      timeout: 60000,
      request: requestId,
      userId,
      onUploadstarted(uploadInfo: unknown) {
        uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
      },
      onUploadSucceed() {
        resolve({
          fileUrl: bucketFileName || '',
          url: '',
        })
      },
      onUploadFailed(_uploadInfo: unknown, code: string, msg: string) {
        reject(new Error(msg || code || '视频上传失败'))
      },
      onUploadProgress() {},
    })

    uploader.addFile(file)
    uploader.startUpload()
  })
}

function isAliyunVodPrepareResult(prepare: DashboardUploadPrepareResult): boolean {
  return Boolean(prepare.uploadAuth && prepare.uploadAddress && prepare.videoId)
}

function isTencentVodPrepareResult(prepare: DashboardUploadPrepareResult): boolean {
  return prepare.vodType === 'tencent' && Boolean(prepare.uploadAuth)
}

function isVodPrepareResult(prepare: DashboardUploadPrepareResult): boolean {
  return isAliyunVodPrepareResult(prepare) || isTencentVodPrepareResult(prepare)
}

async function uploadViaTencentVod(
  file: File,
  prepare: DashboardUploadPrepareResult,
): Promise<{ fileUrl: string; url: string }> {
  await loadTencentVodSdk()

  const TcVodModule = (window as unknown as Record<string, unknown>).TcVod as Record<string, unknown> | undefined
  if (!TcVodModule) {
    throw new Error('腾讯云视频上传SDK不可用')
  }
  // CDN 加载时导出 TcVod.default，直接 import 时导出 TcVod
  const TcVod = (TcVodModule.default || TcVodModule) as
    { new (config: { getSignature: () => Promise<string> }): { upload: (opts: { mediaFile: File; mediaName?: string }) => TencentVodUploader } }

  // Cache the signature from the prepare response (valid for 2 days)
  let cachedSignature: string = prepare.uploadAuth || ''

  return new Promise((resolve, reject) => {
    const tcVod = new TcVod({
      getSignature() {
        if (cachedSignature) {
          return Promise.resolve(cachedSignature)
        }
        // Fallback: request a fresh signature from the backend
        return dashboardPost<string>(DASHBOARD_API.materials.vodSignature)
          .then((sig) => {
            cachedSignature = sig
            return sig
          })
      },
    })

    const uploader = tcVod.upload({
      mediaFile: file,
      mediaName: file.name,
    })

    uploader.done().then(
      (result: TencentVodUploadResult) => {
        const fileId = result.fileId || ''
        // 上传完成后触发任务流（封面截图等），放后台异步执行不阻塞
        if (fileId && prepare.procedure) {
          dashboardPost<string>(DASHBOARD_API.materials.vodProcess, { fileId })
            .then((taskId) => console.log('[TencentVOD] 任务流已触发:', prepare.procedure, 'taskId:', taskId))
            .catch((err) => console.warn('[TencentVOD] 触发任务流失败:', err))
        }
        resolve({
          fileUrl: fileId,
          url: result.video?.url || '',
        })
      },
      (err: { message?: string }) => {
        reject(new Error(err?.message || '腾讯云视频上传失败'))
      },
    )
  })
}

interface TencentVodUploader {
  done: () => Promise<TencentVodUploadResult>
}

interface TencentVodUploadResult {
  fileId?: string
  video?: {
    url?: string
  }
}

export async function uploadDashboardFile(file: File, fallbackType: MaterialFilterType): Promise<UploadedDashboardFile> {
  const materialType = detectDashboardMaterialType(file, fallbackType === 'all' ? 'file' : toDashboardMaterialType(fallbackType))
  // 是否走 VOD 上传通道：统一由后端 config（/agent-api/config → getConfig()）控制，
  // 资源库上传开关 use_vod_in_material_library=false 时视频/音频也走普通 COS/OSS。
  const cfg = await getConfig()
  const useVod = cfg.useVodInMaterialLibrary ?? true
  const isVod = useVod && (materialType === 'video' || materialType === 'audio')
  const prepare = isVod
    ? await dashboardPost<DashboardUploadPrepareResult>(DASHBOARD_API.materials.uploadVodPrepare, { fileName: file.name })
    : await dashboardPost<DashboardUploadPrepareResult>(DASHBOARD_API.materials.uploadFilePrepare(materialType), { fileName: file.name })

  let uploaded: { fileUrl: string; url: string }
  let storageType = 'COS_TENCENT'
  let recordKey = ''

  if (isVod && isVodPrepareResult(prepare)) {
    if (isTencentVodPrepareResult(prepare)) {
      // Tencent VOD path: use Tencent VOD web SDK for direct client-side upload
      uploaded = await uploadViaTencentVod(file, prepare)
      storageType = 'VOD_TENCENT'
      recordKey = uploaded.fileUrl || ''
    } else {
      // Aliyun VOD path: use the VOD upload SDK for resumable chunked upload
      uploaded = await uploadViaAliyunVod(file, prepare)
      storageType = 'VOD_ALIYUN'
      recordKey = prepare.videoId || uploaded.fileUrl || ''
    }
  } else {
    // Generic STS or COS path: use direct OSS/COS upload
    uploaded = await uploadDirectFile(file, {
      ...prepare,
      dir: prepare.dir || '',
      filename: prepare.filename || file.name,
    })
    storageType = prepare.provider === 'cos' || prepare.provider === 'tencent' ? 'COS_TENCENT' : 'OSS_ALIYUN'
    recordKey = uploaded.fileUrl || ''
  }

  return {
    name: file.name,
    size: file.size,
    fileUrl: uploaded.fileUrl,
    url: uploaded.url,
    materialType,
    storageType,
    key: recordKey,
  }
}

function createBlobAxiosResponse(data: Blob): AxiosResponse<Blob> {
  return {
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {},
  } as AxiosResponse<Blob>
}

export interface RecordMaterialsParam {
  dirId?: string | null
  materialType: DashboardMaterialType
  storageType: string
  forceUpdate?: boolean
  items: Array<Record<string, unknown>>
}

/**
 * 统一入库入口：调用本地 Python /agent-api/materials/record。
 * 云端（server）由 Python 转发到 Java /mainD/material/record，桌面本地直接落库；
 * 返回 Java Result<T> 统一信封 {code, msg, data, success}，data 为 MaterialRecordResult
 * （items[] + successCount/failCount），每条 item 为 MaterialItemResponse + success/errorMsg。
 */
export async function recordMaterials(param: RecordMaterialsParam): Promise<MaterialRecordResult> {
  const response = await apiClient.post<MaterialRecordEnvelope | MaterialRecordResult>(API.materials.record, {
    idParentDir: param.dirId ?? null,
    materialType: param.materialType,
    storageType: param.storageType,
    forceUpdate: param.forceUpdate ?? false,
    items: (param.items ?? []).map((item) => ({
      ...item,
      // 就地清洗素材名，避免超长/带电商 CDN 特殊字段的名称触发后端 422
      name: sanitizeMaterialName(String(item.name ?? '')),
    })),
  })

  const body = response.data
  // 统一信封：{code, msg, data, success}；解包 data
  if (isRecord(body) && 'data' in body) {
    const envelope = body as MaterialRecordEnvelope
    const code = Number(envelope.code)
    if (Number.isFinite(code) && code >= 200 && code < 300 && envelope.data) {
      return envelope.data
    }
    throw new Error(envelope.msg || '入库失败')
  }
  // 兜底：直接返回裸结构（旧后端）
  return body as MaterialRecordResult
}

export const localMaterialsApi = {
  stats: async () => {
    const response = await apiClient.get<MaterialStatsResponse>(API.materials.stats)
    return response.data
  },

  /** 只读扫描指定本地目录，返回占用空间（字节）与文件数量 */
  dirStats: async (directory: string) => {
    const response = await apiClient.get<MaterialStatsResponse>(API.materials.dirStats, {
      params: { directory },
    })
    return response.data
  },

  listDirs: async () => {
    const response = await apiClient.get<MaterialDirNode[]>(API.materials.dirs)
    return response.data
  },

  createDir: async (payload: { name: string; parentId?: string }) => {
    const response = await apiClient.post<MaterialDirNode>(API.materials.dirs, {
      name: payload.name,
      parent_id: payload.parentId,
    })
    return response.data
  },

  updateDir: async (dirId: string, name: string, parentId?: string) => {
    void parentId
    const response = await apiClient.put<MaterialDirNode>(`${API.materials.dirs}/${dirId}`, {
      name,
    })
    return response.data
  },

  deleteDir: async (dirId: string) => {
    await apiClient.delete(`${API.materials.dirs}/${dirId}`)
  },

  list: async (params: { materialType: MaterialFilterType; dirId?: string; query?: string; page: number; pageSize: number }) => {
    const response = await apiClient.get<MaterialListResponse<DesktopMaterialItem>>(API.materials.base, {
      params: {
        material_type: params.materialType === 'all' ? undefined : params.materialType,
        dir_id: params.dirId,
        query: params.query,
        page: params.page,
        page_size: params.pageSize,
      },
    })
    return response.data
  },

  upload: async (params: { materialType?: MaterialFilterType; dirId?: string; files: File[] }) => {
    const formData = new FormData()
    // materialType is ignored — type is auto-detected from file extension server-side
    if (params.dirId) {
      formData.append('dir_id', params.dirId)
    }
    params.files.forEach((file) => formData.append('files', file))

    const response = await apiClient.post<DesktopMaterialItem[]>(API.materials.base, formData)
    // 桌面版本地文件操作：上传后文件立即可用，无需标记 processing 状态
    return response.data
  },

  updateItem: async (itemId: string, payload: { name?: string; dirId?: string }) => {
    const response = await apiClient.put<DesktopMaterialItem>(`${API.materials.base}/${itemId}`, {
      name: payload.name,
      dir_id: payload.dirId,
    })
    return response.data
  },

  moveItems: async (ids: string[], dirId: string) => {
    const response = await apiClient.post<{ moved: number }>(API.materials.move, {
      ids,
      dir_id: dirId,
    })
    return response.data
  },

  copyItems: async (ids: string[], dirId: string) => {
    const response = await apiClient.post<{ copied: number }>(API.materials.copy, {
      ids,
      dir_id: dirId,
    })
    return response.data
  },

  deleteItems: async (ids: string[]) => {
    const response = await apiClient.delete<{ deleted: number }>(API.materials.base, {
      data: { ids },
    })
    return response.data
  },

  downloadFile: async (itemId: string): Promise<AxiosResponse<Blob>> => {
    return apiClient.get(API.materials.file(itemId), {
      responseType: 'blob',
      params: { download: true },
    })
  },

  /**
   * 把本地素材文件拉成 Blob（自动带 Authorization 头）。
   * 用于桌面 WebView 里用 URL.createObjectURL(blob) 播放视频/音频，
   * 绕开 WebView 对本地 HTTP 媒体流（HEAD/Range/跨源）的兼容问题。
   */
  fetchFileAsBlob: async (itemId: string): Promise<Blob> => {
    const response = await apiClient.get(API.materials.file(itemId), {
      responseType: 'blob',
      params: { inline: true },
    })
    return response.data
  },

  sync: async (directory?: string): Promise<{ created: number; restored: number; updated: number; deleted: number; total_disk: number; total_db: number }> => {
    const response = await apiClient.post<{ created: number; restored: number; updated: number; deleted: number; total_disk: number; total_db: number }>(API.materials.sync, { directory })
    return response.data
  },

  getContent: async (itemId: string) => {
    const response = await apiClient.get<{ cached_content: string }>(`${API.materials.base}/${itemId}/content`)
    return response.data
  },

  getPlayUrl: async (item: DesktopMaterialItem): Promise<string> => {
    // 桌面版：视频/音频通过本地后端 /agent-api/materials/{id}/file?inline=true 直接播放
    // 文件已在本地磁盘，无需转码处理，preview_url 已含正确的 inline 访问路径
    if (item.material_type === 'video' || item.material_type === 'audio') {
      return item.preview_url || item.download_url || ''
    }
    if (item.material_type === 'image') {
      return item.preview_url || item.download_url || ''
    }
    return item.preview_url || item.download_url || ''
  },
}

export const dashboardMaterialsApi = {
  stats: async (): Promise<MaterialStatsResponse> => {
    const data = await dashboardPost<DashboardMaterialInfo>(DASHBOARD_API.materials.info)
    return {
      used_size: Number(data?.usedSize ?? 0),
      file_num: Number(data?.fileNum ?? 0),
    }
  },

  listDirs: async (materialType: MaterialFilterType, materialTypes?: MaterialFilterType[]): Promise<MaterialDirNode[]> => {
    const dashboardTypes = materialTypes
      ? materialTypes.map((t) => MATERIAL_TYPE_TO_DASHBOARD[t] ?? 'file')
      : undefined
    const filterType = dashboardTypes ? undefined : toDashboardMaterialFilter(materialType)
    const body: Record<string, unknown> = {}
    if (dashboardTypes && dashboardTypes.length > 0) {
      body.materialTypeList = dashboardTypes
    } else if (filterType) {
      body.materialType = filterType
    }
    const data = await dashboardPost<DashboardDirNode[]>(DASHBOARD_API.materials.dirTree, Object.keys(body).length > 0 ? body : undefined)
    return (data ?? []).map((node) => normalizeDir(node, materialType === 'all' ? 'file' : materialType))
  },

  createDir: async (payload: { name: string; materialType: MaterialFilterType; parentId?: string }): Promise<MaterialDirNode> => {
    const data = await dashboardPost<DashboardDirNode>(DASHBOARD_API.materials.dirEdit, {
      idParentDir: toDashboardId(payload.parentId),
      name: payload.name,
    })
    return normalizeDir(data ?? { name: payload.name, idParentDir: toDashboardId(payload.parentId) }, payload.materialType === 'all' ? 'file' : payload.materialType)
  },

  updateDir: async (dirId: string, name: string, parentId?: string): Promise<MaterialDirNode> => {
    const data = await dashboardPost<DashboardDirNode>(DASHBOARD_API.materials.dirEdit, {
      id: toDashboardId(dirId),
      idParentDir: toDashboardId(parentId),
      name,
    })
    return normalizeDir(data ?? { idDir: dirId, name }, 'file')
  },

  deleteDir: async (dirId: string) => {
    await dashboardPost<unknown>(DASHBOARD_API.materials.dirDelete, {
      id: toDashboardId(dirId),
    })
  },

  list: async (params: { materialType?: MaterialFilterType; dirId?: string; query?: string; page: number; pageSize: number; materialTypes?: MaterialFilterType[] }): Promise<MaterialListResponse> => {
    // Build request: prefer multi-type materialTypeList when provided
    const dashboardTypes = params.materialTypes
      ? params.materialTypes.map((t) => MATERIAL_TYPE_TO_DASHBOARD[t] ?? 'file')
      : undefined
    const filterType = dashboardTypes ? undefined : toDashboardMaterialFilter(params.materialType ?? 'all')
    const request: Record<string, unknown> = {
      idParentDir: toDashboardId(params.dirId),
      page: params.page,
      rows: params.pageSize,
    }
    if (dashboardTypes && dashboardTypes.length > 0) {
      request.materialTypeList = dashboardTypes
    } else if (filterType) {
      request.materialType = filterType
    }
    const [count, list] = await Promise.all([
      dashboardPost<number | string>(DASHBOARD_API.materials.count, request),
      dashboardPost<DashboardMaterialRecord[]>(DASHBOARD_API.materials.list, request),
    ])
    const fallbackType = (params.materialType === 'all' || !params.materialType) ? 'file' : params.materialType
    const normalized = (list ?? [])
      .map((item) => normalizeItem(item, fallbackType))
      .filter((item) => !params.query || item.name.includes(params.query))
    return {
      items: normalized,
      total: params.query ? normalized.length : Number(count ?? 0),
    }
  },

  upload: async (params: { materialType: MaterialFilterType; dirId?: string; files: File[] }): Promise<MaterialItemResponse[]> => {
    const uploadedFiles = await Promise.all(params.files.map((file) => uploadDashboardFile(file, params.materialType)))
    const groups = uploadedFiles.reduce((acc, file) => {
      const storageType = file.storageType || 'COS_TENCENT'
      const groupKey = `${file.materialType}:${storageType}`
      const group = acc.get(groupKey) ?? {
        materialType: file.materialType,
        storageType,
        items: [] as Array<Record<string, unknown>>,
      }
      const item: Record<string, unknown> = {
        fileSize: file.size,
        name: file.name,
        key: file.key || file.fileUrl || '',
      }
      group.items.push(item)
      acc.set(groupKey, group)
      return acc
    }, new Map<string, { materialType: DashboardMaterialType; storageType: string; items: Array<Record<string, unknown>> }>())

    // 统一入库入口：Python /agent-api/materials/record（云端转发 Java，桌面本地落库）
    const results = await Promise.all(Array.from(groups.values()).map((group) => recordMaterials({
      dirId: params.dirId,
      materialType: group.materialType,
      storageType: group.storageType,
      items: group.items,
    })))
    const recorded = results.flatMap((r) => r.items)

    // 成功条优先用后端返回的真实 id；后端未返回（旧逻辑兜底）时用前端合成 id
    return uploadedFiles.map((file, index) => {
      const matched = recorded[index]
      const base = matched && matched.id ? {
        id: matched.id,
        file_url: matched.file_url ?? file.fileUrl ?? null,
        file_url_show: matched.file_url_show ?? file.url ?? null,
        front_url: matched.front_url ?? null,
        front_url_show: matched.front_url_show ?? null,
        storage_type: matched.storage_type ?? file.storageType ?? null,
      } : {
        id: `${Date.now()}-${index}`,
        file_url: file.fileUrl || null,
        file_url_show: file.url || null,
        // 刚上传的视频/音频还没有封面（VOD 任务流异步生成 frontUrl），front_url 置空，
        // 界面显示默认占位图，绝不把媒体文件地址当封面 <img> src。
        front_url: null,
        front_url_show: null,
        storage_type: file.storageType || null,
      }
      return {
        id: base.id,
        name: file.name,
        material_type: toMaterialType(file.materialType),
        dir_id: params.dirId ?? null,
        file_size: file.size,
        file_type: getFileExtension(params.files[index]) || null,
        ext: getFileExtension(params.files[index]) || null,
        ...base,
      }
    })
  },

  uploadByUrl: async (params: { urls: string[]; dirId?: string }): Promise<MaterialItemResponse[]> => {
    // Step 1: server downloads images from URLs and uploads to storage
    // Response fields: bucketFileName (tencent://...), url (CDN), name, fileSize, width, height
    let uploadedList: Array<Record<string, unknown>>
    try {
      uploadedList = await dashboardPost<Array<Record<string, unknown>>>(DASHBOARD_API.materials.imgsWithUrl, {
        urls: params.urls,
      })
    } catch (error) {
      // Detect CDN anti-leech / image-not-downloadable errors
      const respMsg = String(
        (error as { response?: { data?: unknown } })?.response?.data
        ?? (error as { message?: string })?.message
        ?? ''
      )
      if (respMsg.includes('image content is not legal') || respMsg.includes('ImageCheck') || respMsg.includes('COS write file')) {
        throw new Error(
          '网络图片上传失败：该图片来源有防盗链保护（CDN IP 白名单），服务器无法直接下载。\n\n请在浏览器中打开图片 → 右键「图片另存为」保存到本地 → 通过「上传本地资源」按钮导入。'
        )
      }
      throw error
    }

    if (!uploadedList?.length) {
      throw new Error('网络图片上传失败：服务器未返回上传结果')
    }

    // Step 2: group by storage type and call /record to create DB records
    const groups = uploadedList.reduce((acc, file) => {
      // imgsWithUrl returns bucketFileName (e.g. "tencent://material/..."), not bare key
      const bucketFileName = String(file.bucketFileName ?? '')
      // Detect storage type from the prefix and strip it to get the bare key
      let storageType: string
      let key = bucketFileName
      if (bucketFileName.startsWith('tencent://')) {
        storageType = 'COS_TENCENT'
        key = bucketFileName.slice('tencent://'.length)
      } else if (bucketFileName.startsWith('aliyun://')) {
        storageType = 'OSS_ALIYUN'
        key = bucketFileName.slice('aliyun://'.length)
      } else {
        storageType = 'OSS_ALIYUN'
      }
      const groupKey = `img:${storageType}`
      const group = acc.get(groupKey) ?? {
        materialType: 'img' as DashboardMaterialType,
        storageType,
        items: [] as Array<Record<string, unknown>>,
      }
      group.items.push({
        fileSize: Number(file.fileSize ?? 0),
        name: String(file.name || '网络图片'),
        key,
      })
      acc.set(groupKey, group)
      return acc
    }, new Map<string, { materialType: DashboardMaterialType; storageType: string; items: Array<Record<string, unknown>> }>())

    await Promise.all(Array.from(groups.values()).map((group) => recordMaterials({
      dirId: params.dirId,
      materialType: group.materialType,
      storageType: group.storageType,
      items: group.items,
    })))

    return (uploadedList ?? []).map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: String(file.name || '网络图片'),
      material_type: 'image' as const,
      dir_id: params.dirId ?? null,
      file_size: Number(file.fileSize ?? 0),
      file_type: null,
      ext: null,
      file_url: String(file.bucketFileName || ''),
      file_url_show: String(file.url || ''),
      front_url: null,
      front_url_show: null,
    }))
  },

  updateItem: async (itemId: string, payload: { name?: string; dirId?: string }): Promise<MaterialItemResponse> => {
    const data = await dashboardPost<DashboardMaterialRecord>(DASHBOARD_API.materials.edit, {
      id: toDashboardId(itemId),
      idParentDir: toDashboardId(payload.dirId),
      name: payload.name,
    })
    return normalizeItem(data ?? { idDir: itemId, name: payload.name, idParentDir: payload.dirId }, 'file')
  },

  moveItems: async (ids: string[], dirId: string): Promise<{ moved: number }> => {
    await dashboardPost<unknown>(DASHBOARD_API.materials.move, {
      ids: ids.map(toDashboardId),
      idParentDir: toDashboardId(dirId),
    })
    return { moved: ids.length }
  },

  copyItems: async (ids: string[], dirId: string): Promise<{ copied: number }> => {
    await dashboardPost<unknown>(DASHBOARD_API.materials.copy, {
      ids: ids.map(toDashboardId),
      idParentDir: toDashboardId(dirId),
    })
    return { copied: ids.length }
  },

  deleteItems: async (ids: string[]): Promise<{ deleted: number }> => {
    await dashboardPost<unknown>(DASHBOARD_API.materials.delete, {
      ids: ids.map(toDashboardId),
    })
    return { deleted: ids.length }
  },

  downloadFile: async (itemId: string, item?: { name?: string; material_type?: MaterialType }): Promise<AxiosResponse<Blob>> => {
    const uploadType = item?.material_type ? MATERIAL_TYPE_TO_DASHBOARD[item.material_type] : undefined
    const result = await dashboardPost<string | { url?: string }>(DASHBOARD_API.materials.download, {
      id: toDashboardId(itemId),
      ...(uploadType && { uploadType }),
      ...(item?.name && { filename: item.name }),
    })
    const url = typeof result === 'string' ? result : result?.url
    if (!url) {
      throw new Error('未获取到下载地址')
    }
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`下载失败：${response.status}`)
    }
    return createBlobAxiosResponse(await response.blob())
  },

  sync: async (): Promise<{ created: number; restored: number; deleted: number; total_disk: number; total_db: number }> => {
    return { created: 0, restored: 0, deleted: 0, total_disk: 0, total_db: 0 }
  },

  getContent: async () => {
    return { cached_content: '' }
  },

  getPlayUrl: async (item: MaterialItemResponse): Promise<string> => {
    if (item.material_type === 'video') {
      return dashboardPost<string>(DASHBOARD_API.materials.videoPlay(item.id))
    }
    if (item.material_type === 'audio') {
      return dashboardPost<string>(DASHBOARD_API.materials.audioPlay(item.id))
    }
    return item.file_url_show || item.front_url_show || item.file_url || ''
  },
}

// Default: /materials page uses Dashboard API (port 9100 /mainD/material/*)
// /desktop-materials page should import localMaterialsApi directly
export const materialsApi = dashboardMaterialsApi
